import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Spinner from '@/components/ui/Spinner';
import aresVideo from '@/assets/ares_fleet.mp4';
import prmLogo from '@/assets/prm-claro.png';

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Ingresa usuario y contraseña');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login({ username, password });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message.includes('401')
            ? 'Credenciales incorrectas'
            : err.message
          : 'Error al iniciar sesión'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center overflow-hidden relative bg-[#0a0a12]">
      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-12px) scale(1.02); }
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 40px rgba(139,92,246,.5), 0 0 80px rgba(139,92,246,.25), 0 25px 50px rgba(0,0,0,.4);
          }
          50% {
            box-shadow: 0 0 60px rgba(139,92,246,.7), 0 0 120px rgba(139,92,246,.4), 0 25px 50px rgba(0,0,0,.4);
          }
        }

        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes fade-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Override browser autofill styles */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px rgba(10,10,30,1) inset !important;
          -webkit-text-fill-color: white !important;
          caret-color: white !important;
          transition: background-color 5000s ease-in-out 0s;
        }

        .float-logo {
          animation: float 4s ease-in-out infinite;
          filter: drop-shadow(0 0 24px rgba(65, 229, 169, 0.3));
        }

        .gradient-btn {
          background-size: 200% 200%;
          animation: gradient-flow 4s ease infinite;
        }

        .fade-up { animation: fade-up 0.7s ease-out forwards; }
        .fade-up-1 { animation: fade-up 0.7s ease-out 0.1s forwards; opacity: 0; }
        .fade-up-2 { animation: fade-up 0.7s ease-out 0.2s forwards; opacity: 0; }
        .fade-up-3 { animation: fade-up 0.7s ease-out 0.3s forwards; opacity: 0; }
      `}</style>

      {/* Video Background */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute w-full h-full object-cover"
          style={{ filter: 'brightness(0.4) saturate(1.2)' }}
        >
          <source src={aresVideo} type="video/mp4" />
        </video>

        {/* Gradient overlays for readability */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(10,10,18,0.3) 0%, rgba(10,10,18,0.8) 70%, rgba(10,10,18,0.95) 100%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-[50%]"
          style={{
            background: 'linear-gradient(to top, rgba(10,10,18,1) 0%, transparent 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[400px] px-6">
        {/* Header */}
        <div className="text-center mb-8 fade-up">
          {/* Animated Logo */}
          <img
            src={prmLogo}
            alt="PR Mob Predictive Mobility"
            className="float-logo w-[280px] max-w-full h-auto mx-auto mb-6 rounded-xl"
          />
          <h1 className="text-[26px] font-bold text-white tracking-tight mb-2">
            PR Mob
          </h1>
          <p className="text-sm text-white/50">
            Sistema de seguridad y monitoreo de flotas
          </p>
        </div>

        {/* Card */}
        <div className="relative fade-up-1">
          {/* Shimmer border */}
          <div
            className="shimmer absolute inset-[-1px] rounded-2xl pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,.3), rgba(139,92,246,.5), rgba(192,38,211,.3))',
              WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              padding: 1,
            }}
          />

          <div
            className="relative rounded-2xl p-6 backdrop-blur-2xl"
            style={{
              background: 'rgba(15,12,41,0.7)',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.05)',
            }}
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm text-center">
                  {error}
                </div>
              )}

              {/* Username */}
              <div className="space-y-2 fade-up-2">
                <label className="block text-sm font-medium text-white/80">
                  Usuario
                </label>
                <div
                  className="group h-12 rounded-xl flex items-center gap-3 px-4 transition-all duration-300 focus-within:ring-2 focus-within:ring-purple-500/50 focus-within:border-purple-500/30"
                  style={{
                    background: 'rgba(10,10,30,0.6)',
                    border: '1px solid rgba(255,255,255,.08)',
                  }}
                >
                  <svg className="w-[18px] h-[18px] text-white/40 group-focus-within:text-purple-400 transition-colors" viewBox="0 0 24 24" fill="none">
                    <path d="M20 21c0-3.6-3.6-6.5-8-6.5S4 17.4 4 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.6" />
                  </svg>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="flex-1 bg-transparent border-0 outline-none text-white text-sm placeholder:text-white/30"
                    placeholder="nombre de usuario"
                    autoFocus
                    autoComplete="username"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2 fade-up-3">
                <label className="block text-sm font-medium text-white/80">
                  Contraseña
                </label>
                <div
                  className="group h-12 rounded-xl flex items-center gap-3 px-4 transition-all duration-300 focus-within:ring-2 focus-within:ring-purple-500/50 focus-within:border-purple-500/30"
                  style={{
                    background: 'rgba(10,10,30,0.6)',
                    border: '1px solid rgba(255,255,255,.08)',
                  }}
                >
                  <svg className="w-[18px] h-[18px] text-white/40 group-focus-within:text-purple-400 transition-colors" viewBox="0 0 24 24" fill="none">
                    <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
                  </svg>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1 bg-transparent border-0 outline-none text-white text-sm placeholder:text-white/30"
                    placeholder="contraseña"
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  {password && (
                    <svg className="w-[18px] h-[18px] text-emerald-400" viewBox="0 0 24 24" fill="none">
                      <path d="M20 7 10 17l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Button */}
              <button
                type="submit"
                disabled={loading}
                className="gradient-btn w-full h-12 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2.5 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-500/20 active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 mt-2"
                style={{
                  background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #c026d3, #8b5cf6, #6366f1)',
                  backgroundSize: '200% 100%',
                  border: '1px solid rgba(255,255,255,.1)',
                }}
              >
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M13 6l6 6-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Iniciar Sesión
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/25 text-xs mt-8 fade-up-3">
          PR Mob © 2026
        </p>
      </div>
    </div>
  );
}
